/**
 * LandingTemplate — generic programmatic-SEO landing.
 *
 * Each page under /programar-*, /alternativa-a-*, /redes-sociales-* etc. is just
 * a small data file that exports a `LandingData` object and renders <LandingTemplate
 * data={...} />. The template handles SEO, layout and shared sections (navbar,
 * hero, "why us", pricing, FAQ, final CTA, footer) so adding a new landing is
 * a 5-minute task.
 */
import { Link } from 'react-router-dom';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { ArrowRight, ChevronDown, Check } from 'lucide-react';
import { Navbar } from './Navbar';
import { SiteFooter } from './SiteFooter';
import { PricingPlans } from './PricingPlans';
import { useSeo, type PageSeo } from '../lib/seo';
import { zettapostSteps, type LandingGuide } from '../lib/landingGuides';

export type LandingFaq = { q: string; a: string };

export type LandingFeature = {
  Icon: ComponentType<LucideProps>;
  title: string;
  description: string;
};

export type LandingData = {
  /** Page-level SEO. Same shape used by `useSeo()`. */
  seo: PageSeo;
  hero: {
    /** Small label above the H1 (e.g. "Instagram"). */
    eyebrow: string;
    /** First half of the H1 (rendered in slate-900). */
    headlinePre: string;
    /** Second half of the H1 (rendered in brand-600). */
    headlineAccent: string;
    subhead: string;
    /** Short bullet list shown under the subhead — micro social-proof. */
    bullets?: string[];
  };
  why: {
    eyebrow: string;
    title: string;
    subtitle?: string;
    items: LandingFeature[];
  };
  faqs: LandingFaq[];
  /** "Cómo programar en <red>" walkthrough; its FAQs are appended to `faqs`. */
  guide?: LandingGuide;
  finalCta?: {
    title: string;
    titleAccent?: string;
    description?: string;
  };
  /** Cross-links to other landings — internal linking helps SEO. */
  related?: Array<{ to: string; label: string }>;
};

export function LandingTemplate({ data }: { data: LandingData }) {
  useSeo(data.seo);

  const finalCta = data.finalCta ?? {
    title: 'Empieza ahora,',
    titleAccent: 'es gratis durante 3 días.',
    description: 'Crea tu cuenta y pruébanos.',
  };

  const faqs = [...data.faqs, ...(data.guide?.faqs ?? [])];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white overflow-x-hidden">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="relative bg-gradient-to-b from-slate-50 via-white to-white">
          <div className="max-w-5xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 text-center">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-3 py-1">
              {data.hero.eyebrow}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[56px] font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              {data.hero.headlinePre}{' '}
              <span className="text-brand-600">{data.hero.headlineAccent}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {data.hero.subhead}
            </p>

            {data.hero.bullets && data.hero.bullets.length > 0 && (
              <ul className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-slate-600">
                {data.hero.bullets.map((b) => (
                  <li key={b} className="inline-flex items-center gap-1.5">
                    <Check size={15} className="text-brand-600 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="btn-accent text-base px-7 py-3.5 gap-2 group rounded-xl">
                Probar gratis 3 días
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
              <Link to="/precios" className="btn-secondary text-base px-6 py-3.5 rounded-xl">
                Ver precios
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Cancela cuando quieras.</p>
          </div>
        </section>

        {/* ── WHY ──────────────────────────────────────────────── */}
        <section className="bg-white py-20 lg:py-24 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">
                {data.why.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">{data.why.title}</h2>
              {data.why.subtitle && <p className="mt-3 text-slate-600">{data.why.subtitle}</p>}
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.why.items.map((item) => (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-brand-200 hover:shadow-sm transition"
                >
                  <div className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <item.Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-bold text-slate-900 leading-snug">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── GUIDE ────────────────────────────────────────────── */}
        {data.guide && <GuideSection guide={data.guide} />}

        {/* ── PRICING ──────────────────────────────────────────── */}
        <section id="precios" className="bg-slate-50 py-20 lg:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Precios</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
                Elige el plan<br />
                <span>que mejor encaje contigo.</span>
              </h2>
              <p className="mt-3 text-slate-600">
                Sin permanencia. Cambia o cancela cuando quieras. Empieza con 3 días gratis.
              </p>
            </div>
            <PricingPlans />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────── */}
        <section className="bg-white py-20 lg:py-28">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Preguntas frecuentes</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
                Lo que más nos preguntan.
              </h2>
            </div>

            <div className="mt-10 space-y-3">
              {faqs.map((item, i) => (
                <details
                  key={i}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-colors open:shadow-md open:border-brand-200"
                >
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 sm:px-6 sm:py-5">
                    <span className="font-semibold text-slate-900 text-base sm:text-lg leading-snug">
                      {item.q}
                    </span>
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 group-open:bg-brand-100 group-open:text-brand-700 text-slate-500 transition-all">
                      <ChevronDown size={18} className="transition-transform duration-300 group-open:rotate-180" />
                    </span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 -mt-1">
                    <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                      {item.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── RELATED LANDINGS (internal linking) ──────────────── */}
        {data.related && data.related.length > 0 && (
          <section className="bg-slate-50 border-t border-slate-200 py-14">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-4">
                Te puede interesar
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {data.related.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-full px-4 py-2 hover:border-brand-300 hover:text-brand-700 transition"
                  >
                    {r.label}
                    <ArrowRight size={13} />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── FINAL CTA ────────────────────────────────────────── */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-slate-50" />
          <div className="max-w-3xl mx-auto px-6 py-20 lg:py-24 text-center">
            <div className="flex justify-center mb-6">
              <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-10 w-auto" />
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
              {finalCta.title}
              {finalCta.titleAccent && (
                <>
                  <br />
                  <span className="text-brand-600">{finalCta.titleAccent}</span>
                </>
              )}
            </h2>
            {finalCta.description && (
              <p className="mt-5 text-lg text-slate-600">{finalCta.description}</p>
            )}
            <div className="mt-8 flex justify-center">
              <Link to="/register" className="btn-accent text-base px-7 py-3 gap-2 group rounded-xl">
                Crea tu cuenta ahora
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>

        {/* ── FOOTER ───────────────────────────────────────────── */}
        <SiteFooter />
      </div>
    </>
  );
}

function StepList({ steps }: { steps: string[] }) {
  return (
    <ol className="mt-5 space-y-3">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <span className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-full bg-brand-50 text-brand-700 text-sm font-bold">
            {i + 1}
          </span>
          <span className="text-slate-700 leading-relaxed pt-0.5">{step}</span>
        </li>
      ))}
    </ol>
  );
}

function GuideSection({ guide }: { guide: LandingGuide }) {
  return (
    <section className="bg-white py-20 lg:py-24">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Paso a paso</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
            Cómo programar publicaciones en {guide.network}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">{guide.intro}</p>
        </div>

        <div className={`mt-12 grid gap-6 ${guide.native ? 'md:grid-cols-2' : ''}`}>
          {guide.native && (
            <div className="rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900">Desde {guide.network}</h3>
              <StepList steps={guide.native.steps} />
              <p className="mt-5 text-sm text-slate-500 leading-relaxed">{guide.native.note}</p>
            </div>
          )}
          <div className="rounded-2xl border border-brand-200 bg-brand-50/40 p-6">
            <h3 className="text-lg font-bold text-slate-900">Con Zettapost</h3>
            <StepList steps={zettapostSteps(guide)} />
            <Link to="/register" className="mt-6 inline-flex items-center gap-2 text-brand-700 font-semibold hover:text-brand-800">
              Probar 3 días gratis <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="mt-10 rounded-2xl bg-slate-50 p-6">
          <h3 className="text-lg font-bold text-slate-900">Consejos para programar en {guide.network}</h3>
          <ul className="mt-4 space-y-2.5">
            {guide.tips.map((tip, i) => (
              <li key={i} className="flex gap-2.5 text-slate-700 leading-relaxed">
                <Check size={18} className="mt-0.5 shrink-0 text-brand-600" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
